import type { ApiResponse } from '../types/api';
import httpClient from './http/httpClient';

export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed' | 'pending';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TicketCategory = 'technical' | 'billing' | 'feature_request' | 'bug_report' | 'general';

export interface HelpTicket {
  id: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  userId: string;
  assignedTo?: string;
  createdAt: Date;
  updatedAt?: Date;
  responses?: TicketResponse[];
  attachments?: TicketAttachment[];
  timeToFirstResponse?: number;
  lastResponseAuthorType?: 'user' | 'support';
  ticketNumber?: string;
  userName?: string;
  userEmail?: string;
  responseCount?: number;
  clientId?: string;
  clientName?: string;
  teamName?: string;
}

export interface TicketResponse {
  id?: string;
  ticketId?: string;
  message?: string;
  response?: string;
  authorId: string;
  authorName?: string;
  attachments?: TicketAttachment[];
  authorType?: 'user' | 'support' | 'client_admin';
  isSystemMessage?: boolean;
  timestamp: Date | string;
}

export interface TicketAttachment {
  id: string;
  filename: string;
  url: string;
  size: number;
  contentType: string;
  uploadedAt: Date;
}

export interface CreateTicketData {
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  attachments?: { filename: string; url: string }[];
}

export interface TicketFilters {
  status?: TicketStatus;
  category?: TicketCategory;
  priority?: TicketPriority;
  ticketId?: string;
  userId?: string;
  assignedTo?: string;
  limit?: number;
  offset?: number;
}

export interface UpdateTicketData {
  updateType?: 'status' | 'response' | 'assign';
  status?: TicketStatus;
  priority?: TicketPriority;
  assignedTo?: string;
  response?: string;
  internalNotes?: string;
  reason?: string;
  message?: string;
}

export interface TicketsResponse {
  success: boolean;
  tickets?: HelpTicket[];
  ticket?: HelpTicket;
  total?: number;
  message?: string;
}

export const createHelpTicket = async (
  ticketData: CreateTicketData
): Promise<ApiResponse<HelpTicket>> => {
  try {
    const response = await httpClient.post<ApiResponse<HelpTicket>>(
      '/createHelpTicket',
      ticketData
    );

    if (response.success) {
      return response;
    } else {
      throw new Error(response.error?.message || 'Failed to create ticket');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Create ticket failed: ${err.response?.data?.message || err.message}`);
  }
};

export const getHelpTickets = async (filters: TicketFilters = {}): Promise<TicketsResponse> => {
  try {
    const params = new URLSearchParams();

    // Add filters to query params
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.category) {
      params.append('category', filters.category);
    }
    if (filters.priority) {
      params.append('priority', filters.priority);
    }
    if (filters.ticketId) {
      params.append('ticketId', filters.ticketId);
    }
    if (filters.userId) {
      params.append('userId', filters.userId);
    }
    if (filters.assignedTo) {
      params.append('assignedTo', filters.assignedTo);
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters.offset) {
      params.append('offset', filters.offset.toString());
    }

    const queryString = params.toString();
    const url = `/getHelpTickets${queryString ? `?${queryString}` : ''}`;

    const response = await httpClient.get<TicketsResponse>(url);

    if (response.success) {
      return response;
    } else {
      throw new Error(response.message || 'Failed to fetch tickets');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Fetch tickets failed: ${err.response?.data?.message || err.message}`);
  }
};

export const updateHelpTicket = async (
  ticketId: string,
  updateData: UpdateTicketData
): Promise<ApiResponse<HelpTicket>> => {
  try {
    const payload = {
      ticketId,
      ...updateData,
    };

    const response = await httpClient.put<ApiResponse<HelpTicket>>('/updateHelpTicket', payload);

    if (response.success) {
      return response;
    } else {
      throw new Error(response.error?.message || 'Failed to update ticket');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Update ticket failed: ${err.response?.data?.message || err.message}`);
  }
};

export const getHelpTicketById = async (ticketId: string): Promise<HelpTicket> => {
  try {
    const response = await httpClient.get<{
      success: boolean;
      ticket: HelpTicket;
      message?: string;
    }>(`/getHelpTicketById?ticketId=${ticketId}`);

    if (response.success) {
      return response.ticket;
    } else {
      throw new Error(response.message || 'Failed to fetch ticket');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Fetch ticket failed: ${err.response?.data?.message || err.message}`);
  }
};

export const createTicketResponse = async (
  ticketId: string,
  responseText: string
): Promise<ApiResponse<HelpTicket>> => {
  return updateHelpTicket(ticketId, {
    updateType: 'response',
    message: responseText,
  });
};
